import { createSelector } from '@reduxjs/toolkit';
import { CellStatus } from 'admin/src/screens/admin/Pollworker/StaffingChart/enums';
import type { AppState } from 'admin/src/store';
import type {
  StaffingCellData,
  StaffingLocationData,
  StaffingRole,
  StaffingRoleData,
  PartyCount,
  CellStaffingStatus,
  CellStatusMetadata,
} from 'admin/src/screens/admin/Pollworker/StaffingChart/staffingTypes';
import { PARTY_COLORS } from 'admin/src/screens/admin/Pollworker/StaffingChart/staffingTypes';
import dayjs from 'dayjs';
import { ReleaseStatus, type RoleDateConfig } from 'admin/src/types/Pollworker/LocationStaffing';
import { getDatesBetween } from 'admin/src/components/Pollworker/Panels/pollworkerVotingLocationDetailsUtils';
import { generateHslColor } from 'shared/src/utils/colors';
import { MimicPhase, type AddedRole } from 'admin/src/reducers/pollworker/staffing';
import { normalizeWorkDate } from 'admin/src/utils/pollworker/scheduleDates';
import { toSameDayScheduleRef, type SameDayScheduleRef } from 'admin/src/utils/pollworker/sameDaySchedules';
import { selectStaffingPartiesFromGrid, selectAvailableWorkersFromGrid, selectAllAvailableWorkers, loadingPollworkerDataSelector } from 'admin/src/selectors/pollworker/grid';

const EMPTY_SAME_DAY_SCHEDULES: Map<string, SameDayScheduleRef[]> = new Map();

function getPartyCode(partyName: string): string {
  const knownParties: Record<string, string> = {
    Democrat: 'D',
    Republican: 'R',
    Independent: 'I',
    Libertarian: 'L',
    Green: 'G',
  };

  return knownParties[partyName] || partyName.charAt(0).toUpperCase();
}

function getPartyColor(partyName: string, partyCode?: string): string {
  const code = partyCode || partyName.charAt(0).toUpperCase();
  return PARTY_COLORS[code] || generateHslColor(partyName);
}

function transformDateConfigToCell(dateConfig: RoleDateConfig): StaffingCellData {
  const partyBreakdown: PartyCount[] = dateConfig.partyRequirements.map((pr) => ({
    partyCode: getPartyCode(pr.partyName),
    partyId: pr.partyId,
    count: pr.required,
    color: getPartyColor(pr.partyName),
  }));

  return {
    total: dateConfig.totalSlots,
    partyBreakdown: partyBreakdown.length > 0 ? partyBreakdown : undefined,
  };
}

function makeCellKey(locationId: string, roleId: string, date: string): string {
  return `${locationId}|${roleId}|${date}`;
}

function makeScheduleCellKey(locationId: string, roleId: string, date: string): string {
  return `${locationId}-${roleId}-${date}`;
}

const selectStaffingSlice = (state: AppState) => state.pollworker.staffing;

export const selectStaffingResponses = createSelector(selectStaffingSlice, (s) => s.staffingResponses);

export const selectSchedules = createSelector(selectStaffingSlice, (s) => s.schedules);

export const selectUnreleasedScheduleCount = createSelector(selectSchedules, (schedules) => schedules.filter((s) => s.releaseStatus === 0 && !s.isLegacySchedule).length);

export const selectSchedulesByConfirmationStatus = createSelector(selectSchedules, (schedules) => {
  const released = schedules.filter((s) => s.releaseStatus === 1);
  return {
    rejected: released.filter((s) => s.userConfirmationStatus === 2),
    pending: released.filter((s) => s.userConfirmationStatus === 0),
    confirmed: released.filter((s) => s.userConfirmationStatus === 1),
  };
});

export const selectRejectedSchedules = createSelector(selectSchedulesByConfirmationStatus, (byStatus) => byStatus.rejected);

export const selectPendingConfirmationSchedules = createSelector(selectSchedulesByConfirmationStatus, (byStatus) => byStatus.pending);

export const selectAllReleasedSchedules = createSelector(selectSchedulesByConfirmationStatus, (byStatus) => [...byStatus.rejected, ...byStatus.pending, ...byStatus.confirmed]);

export const selectConfirmationStats = createSelector(selectSchedulesByConfirmationStatus, (byStatus) => ({
  rejected: byStatus.rejected.length,
  pending: byStatus.pending.length,
  confirmed: byStatus.confirmed.length,
  total: byStatus.rejected.length + byStatus.pending.length + byStatus.confirmed.length,
}));

export const selectCellConfirmationMetadata = createSelector(selectSchedules, (schedules) => {
  const metadata: Record<string, { rejected: number; pending: number; confirmed: number }> = {};
  for (const s of schedules) {
    if (s.releaseStatus !== 1) continue;
    const key = `${s.locationId}|${s.roleId}|${normalizeWorkDate(s.workDate)}`;
    if (!metadata[key]) metadata[key] = { rejected: 0, pending: 0, confirmed: 0 };
    if (s.userConfirmationStatus === 2) metadata[key].rejected++;
    else if (s.userConfirmationStatus === 0) metadata[key].pending++;
    else metadata[key].confirmed++;
  }
  return metadata;
});

export const selectCellOverrides = createSelector(selectStaffingSlice, (s) => s.cellOverrides);

export const selectAddedRoles = createSelector(selectStaffingSlice, (s) => s.addedRoles);

export const selectRemovedRoles = createSelector(selectStaffingSlice, (s) => s.removedRoles);

export const selectIsLoading = createSelector(selectStaffingSlice, (s) => s.isLoading || s.availability.isLoading);

export const selectIsSaving = createSelector(selectStaffingSlice, (s) => s.isSaving);

export const selectError = createSelector(selectStaffingSlice, (s) => s.error);

export const selectElectionId = createSelector(selectStaffingSlice, (s) => s.electionId);

export const selectRogueConfirmation = createSelector(selectStaffingSlice, (s) => s.rogueConfirmation);

export const selectDateStart = createSelector(selectStaffingSlice, (s) => s.dateStart);

export const selectDateEnd = createSelector(selectStaffingSlice, (s) => s.dateEnd);

export const selectElectionDate = createSelector(selectStaffingSlice, (s) => (s.electionDate ? dayjs.utc(s.electionDate).format('YYYY-MM-DD') : ''));

export const selectPendingSaveLocations = createSelector(selectStaffingSlice, (s) => s.pendingSaveLocations);

export const selectPendingScheduleCells = createSelector(selectStaffingSlice, (s) => s.pendingScheduleCells);

export const selectStaffingParties = selectStaffingPartiesFromGrid;

export const selectStaffingAvailableWorkers = selectAvailableWorkersFromGrid;

export const selectStaffingAllWorkers = selectAllAvailableWorkers;

export const selectInRangeDates = createSelector([selectDateStart, selectDateEnd, selectElectionDate], (dateStart, dateEnd, electionDate) => {
  if (!dateStart || !dateEnd) return [];

  const dates = getDatesBetween(dateStart, dateEnd);

  if (electionDate && !dates.includes(electionDate)) {
    dates.push(electionDate);
    dates.sort();
  }

  return dates;
});

export const selectOutOfRangeDates = createSelector([selectStaffingResponses, selectInRangeDates, selectSchedules], (staffingResponses, inRangeDates, schedules) => {
  const inRangeSet = new Set(inRangeDates);
  const dates = new Set<string>();

  for (const response of Object.values(staffingResponses)) {
    for (const role of response.roles) {
      for (const d of role.dates) {
        dates.add(d.date);
      }
    }
  }

  for (const schedule of schedules) {
    dates.add(normalizeWorkDate(schedule.workDate));
  }

  return Array.from(dates)
    .filter((d) => !inRangeSet.has(d))
    .sort();
});

export const selectAvailableRoles = createSelector([selectStaffingResponses], (staffingResponses) => {
  const roleMap = Object.values(staffingResponses).reduce((roleMap: Map<string, StaffingRole>, response) => {
    for (const role of response.roles) {
      if (roleMap.has(role.roleId)) continue;

      roleMap.set(role.roleId, {
        id: role.roleId,
        roleName: role.roleName,
        active: true,
      });
    }

    return roleMap;
  }, new Map<string, StaffingRole>());

  return Array.from(roleMap.values());
});

export const selectRogueSchedules = createSelector(
  [selectSchedules, selectStaffingResponses, selectInRangeDates, selectOutOfRangeDates],
  (schedules, staffingResponses, inRangeDates, outOfRangeDates) => {
    const displayedDates = new Set([...inRangeDates, ...outOfRangeDates]);

    const existingRoles = new Set<string>();
    for (const response of Object.values(staffingResponses)) {
      for (const role of response.roles) {
        existingRoles.add(`${response.locationId}|${role.roleId}`);
      }
    }

    return schedules.filter((schedule) => {
      if (!displayedDates.has(normalizeWorkDate(schedule.workDate))) return false;
      const key = `${schedule.locationId}|${schedule.roleId}`;
      return !existingRoles.has(key);
    });
  },
);

interface RogueRoleInfo {
  roleId: string;
  roleName: string;
  locationId: string;
  locationName: string;
  dates: Set<string>;
}

export const selectRogueRolesByLocation = createSelector([selectRogueSchedules], (rogueSchedules) => {
  return rogueSchedules.reduce(
    (acc, schedule) => {
      const locKey = schedule.locationId;
      const roleKey = `${locKey}|${schedule.roleId}`;

      if (!acc[locKey]) {
        acc[locKey] = {
          locationId: schedule.locationId,
          locationName: schedule.locationName,
          roles: {},
        };
      }

      if (!acc[locKey].roles[roleKey]) {
        acc[locKey].roles[roleKey] = {
          roleId: schedule.roleId,
          roleName: schedule.roleName,
          locationId: schedule.locationId,
          locationName: schedule.locationName,
          dates: new Set<string>(),
        };
      }

      acc[locKey].roles[roleKey].dates.add(normalizeWorkDate(schedule.workDate));

      return acc;
    },
    {} as Record<string, { locationId: string; locationName: string; roles: Record<string, RogueRoleInfo> }>,
  );
});

export const selectHasStaffingChartData = createSelector(selectStaffingResponses, (responses) => Object.keys(responses).length > 0);

export const selectStaffingChartData = createSelector(
  [selectStaffingResponses, selectCellOverrides, selectAddedRoles, selectRemovedRoles, selectInRangeDates, selectOutOfRangeDates, selectRogueRolesByLocation],
  (staffingResponses, cellOverrides, addedRoles, removedRoles, inRangeDates, outOfRangeDates, rogueRolesByLocation): StaffingLocationData[] => {
    const allDates = [...new Set([...inRangeDates, ...outOfRangeDates])].sort();

    const locationsFromStaffing = Object.values(staffingResponses).map((staffingResponse) => {
      const locationId = staffingResponse.locationId;

      const apiRoles: StaffingRoleData[] = staffingResponse.roles
        .filter((apiRole) => !removedRoles.includes(`${locationId}|${apiRole.roleId}`))
        .map((apiRole, index) => {
          const inRangeCells = inRangeDates.reduce(
            (cells, date) => {
              const cellKey = makeCellKey(locationId, apiRole.roleId, date);

              if (cellOverrides[cellKey]) {
                cells[date] = cellOverrides[cellKey];
                return cells;
              }

              const dateConfig = apiRole.dates.find((d) => d.date === date);
              cells[date] = dateConfig ? transformDateConfigToCell(dateConfig) : { total: 0 };

              return cells;
            },
            {} as Record<string, StaffingCellData>,
          );

          const outOfRangeCells = outOfRangeDates.reduce(
            (cells, date) => {
              const cellKey = makeCellKey(locationId, apiRole.roleId, date);
              if (cellOverrides[cellKey]) {
                cells[date] = cellOverrides[cellKey];
                return cells;
              }

              const dateConfig = apiRole.dates.find((d) => d.date === date);
              cells[date] = dateConfig ? transformDateConfigToCell(dateConfig) : { total: 0 };

              return cells;
            },
            {} as Record<string, StaffingCellData>,
          );

          return {
            roleId: apiRole.roleId,
            role: {
              id: apiRole.roleId,
              roleName: apiRole.roleName,
              active: true,
            },
            instanceIndex: index,
            cells: { ...inRangeCells, ...outOfRangeCells },
            isRogue: false,
          };
        });

      const apiRoleIds = new Set(apiRoles.map((r) => r.roleId));

      const locationAddedRoles: StaffingRoleData[] = addedRoles
        .filter((ar: AddedRole) => ar.locationId === locationId && !apiRoleIds.has(ar.roleId))
        .map((ar: AddedRole) => {
          const cells = allDates.reduce(
            (cells, date) => {
              const cellKey = makeCellKey(locationId, ar.roleId, date);
              cells[date] = cellOverrides[cellKey] || { total: 0 };
              return cells;
            },
            {} as Record<string, StaffingCellData>,
          );

          return {
            roleId: ar.roleId,
            role: { id: ar.roleId, roleName: ar.roleName, active: true },
            instanceIndex: ar.instanceIndex,
            cells,
            isRogue: false,
            isAdded: true,
          };
        });

      const rogueLocationData = rogueRolesByLocation[locationId];
      const rogueRoles: StaffingRoleData[] = rogueLocationData
        ? Object.values(rogueLocationData.roles).map((rogueRole, index) => {
            const cells = allDates.reduce(
              (cells, date) => {
                if (rogueRole.dates.has(date)) {
                  cells[date] = { total: 0 };
                }
                return cells;
              },
              {} as Record<string, StaffingCellData>,
            );

            return {
              roleId: rogueRole.roleId,
              role: {
                id: rogueRole.roleId,
                roleName: rogueRole.roleName,
                active: true,
              },
              instanceIndex: index,
              cells,
              isRogue: true,
            };
          })
        : [];

      const sortedApiRoles = apiRoles.sort((a, b) => a.role.roleName.localeCompare(b.role.roleName));
      const sortedAddedRoles = locationAddedRoles.sort((a, b) => a.role.roleName.localeCompare(b.role.roleName));
      const sortedRogueRoles = rogueRoles.sort((a, b) => a.role.roleName.localeCompare(b.role.roleName));

      return {
        locationId,
        location: {
          id: locationId,
          locationName: staffingResponse.locationName,
        },
        roles: [...sortedApiRoles, ...sortedAddedRoles, ...sortedRogueRoles],
      } as StaffingLocationData;
    });

    const rogueOnlyLocations = Object.entries(rogueRolesByLocation)
      .filter(([locId]) => !staffingResponses[locId])
      .map(([locationId, locData]) => {
        const rogueRoles: StaffingRoleData[] = Object.values(locData.roles)
          .map((rogueRole, index) => {
            const cells = allDates.reduce(
              (cells, date) => {
                if (rogueRole.dates.has(date)) {
                  cells[date] = { total: 0 };
                }
                return cells;
              },
              {} as Record<string, StaffingCellData>,
            );

            return {
              roleId: rogueRole.roleId,
              role: {
                id: rogueRole.roleId,
                roleName: rogueRole.roleName,
                active: true,
              },
              instanceIndex: index,
              cells,
              isRogue: true,
            };
          })
          .sort((a, b) => a.role.roleName.localeCompare(b.role.roleName));

        return {
          locationId,
          location: {
            id: locationId,
            locationName: locData.locationName,
          },
          roles: rogueRoles,
        } as StaffingLocationData;
      });

    return [...locationsFromStaffing, ...rogueOnlyLocations];
  },
);

export const makeSelectIsRoleRogue = () =>
  createSelector(
    [selectStaffingChartData, (_: AppState, locationId: string, _roleId: string) => locationId, (_: AppState, _locationId: string, roleId: string) => roleId],
    (ganttData, locationId, roleId) => {
      const location = ganttData.find((loc) => loc.locationId === locationId);
      const role = location?.roles.find((r) => r.roleId === roleId);
      return role?.isRogue ?? false;
    },
  );

export const makeSelectCellData = () =>
  createSelector(
    [
      selectCellOverrides,
      selectStaffingResponses,
      (_: AppState, locationId: string, _roleId: string, _date: string) => locationId,
      (_: AppState, _locationId: string, roleId: string, _date: string) => roleId,
      (_: AppState, _locationId: string, _roleId: string, date: string) => date,
    ],
    (cellOverrides, responses, locationId, roleId, date) => {
      const key = makeCellKey(locationId, roleId, date);
      if (cellOverrides[key]) return cellOverrides[key];

      const response = responses[locationId];
      const role = response?.roles.find((r) => r.roleId === roleId);
      const dateConfig = role?.dates.find((d) => d.date === date);
      return dateConfig ? transformDateConfigToCell(dateConfig) : { total: 0 };
    },
  );

export const makeSelectCellAssignments = () =>
  createSelector([selectSchedules, (_: AppState, locationId: string, roleId: string, date: string) => [locationId, roleId, date]], (schedules, [locationId, roleId, date]) => {
    const normalizedDate = normalizeWorkDate(date);
    return schedules
      .filter((s) => s.locationId === locationId && s.roleId === roleId && normalizeWorkDate(s.workDate) === normalizedDate)
      .map((s) => ({
        oderId: s.id,
        evUserId: s.evUser.id,
        workerName: `${s.evUser.firstName || ''} ${s.evUser.lastName || ''}`.trim() || 'Unknown',
        partyCode: s.party?.partyCode,
        assignedAt: s.workDate,
        startTime: s.startTime,
        endTime: s.endTime,
        userConfirmationStatus: s.userConfirmationStatus,
        isReleased: s.releaseStatus === ReleaseStatus.Released,
        isLegacySchedule: s.isLegacySchedule,
      }));
  });

export const makeSelectCellPending = () =>
  createSelector(
    [selectPendingScheduleCells, (_: AppState, locationId: string, roleId: string, date: string) => [locationId, roleId, date]],
    (pending, [locationId, roleId, date]) => {
      const key = makeScheduleCellKey(locationId, roleId, date);
      return pending.includes(key);
    },
  );

export const makeSelectCellStatusMetadata = () => {
  const selectCellData = makeSelectCellData();
  const selectCellAssignments = makeSelectCellAssignments();

  return createSelector(
    [
      (state: AppState, locationId: string, roleId: string, date: string) => selectCellData(state, locationId, roleId, date),
      (state: AppState, locationId: string, roleId: string, date: string) => selectCellAssignments(state, locationId, roleId, date),
    ],
    (cellData, assignments): CellStatusMetadata => {
      const required = cellData.total;
      const assigned = assignments.length;

      let partyMet = true;
      let partyNeeds: { code: string; needed: number; have: number }[] | null = null;
      if (cellData.partyBreakdown && cellData.partyBreakdown.length > 0) {
        partyNeeds = [];
        for (const req of cellData.partyBreakdown) {
          const have = assignments.filter((a) => a.partyCode === req.partyCode).length;
          if (have < req.count) {
            partyMet = false;
            partyNeeds.push({ code: req.partyCode, needed: req.count, have });
          }
        }
        if (partyNeeds.length === 0) partyNeeds = null;
      }

      let status: CellStaffingStatus;
      if (cellData.isNotApplicable) {
        status = CellStatus.NOT_APPLICABLE;
      } else if (required === 0) {
        status = assigned > 0 ? CellStatus.OVERSTAFFED : CellStatus.NO_POSITIONS;
      } else if (assigned === 0) {
        status = CellStatus.EMPTY;
      } else if (assigned < required) {
        status = CellStatus.UNDERSTAFFED;
      } else if (assigned > required) {
        status = CellStatus.OVERSTAFFED;
      } else {
        status = CellStatus.FULLY_STAFFED;
      }

      return {
        status,
        required,
        assigned,
        overBy: Math.max(0, assigned - required),
        partyMet,
        partyNeeds,
      };
    },
  );
};

export const selectHighlightEmpty = createSelector(selectStaffingSlice, (s) => s.highlightEmpty);

export const selectFilter = createSelector(selectStaffingSlice, (s) => s.filter);

export const selectFilters = createSelector(selectStaffingSlice, (s) => s.filters);

export const selectChartMode = createSelector(selectStaffingSlice, (s) => s.chartMode);

export const selectDraggedWorker = createSelector(selectStaffingSlice, (s) => s.draggedWorker);

export const selectDragOverCell = createSelector(selectStaffingSlice, (s) => s.dragOverCell);

export const selectExpandedCells = createSelector(selectStaffingSlice, (s) => s.expandedCells);

export const selectSuperCopyActive = createSelector(selectStaffingSlice, (s) => s.superCopyActive);

export const selectSuperCopySourceCell = createSelector(selectStaffingSlice, (s) => s.superCopySourceCell);

export const selectSuperCopySourceCellData = createSelector(selectStaffingSlice, (s) => s.superCopySourceCellData);

export const selectSuperCopySelectedCells = createSelector(selectStaffingSlice, (s) => s.superCopySelectedCells);

export const selectMultiSelectActive = createSelector(selectStaffingSlice, (s) => s.multiSelectActive);

export const selectMultiSelectSelectedCells = createSelector(selectStaffingSlice, (s) => s.multiSelectSelectedCells);

export const selectSelectionAnchor = createSelector(selectStaffingSlice, (s) => s.selectionAnchor);

export const selectActiveStatsTooltip = createSelector(selectStaffingSlice, (s) => s.activeStatsTooltip);

export const selectMultiSelectCellKeys = createSelector(selectMultiSelectSelectedCells, (cells) =>
  cells.map((str) => {
    const [locationId, roleId, date] = str.split('|');
    return { locationId, roleId, date };
  }),
);

export const selectSuperCopyCellKeys = createSelector(selectSuperCopySelectedCells, (cells) =>
  cells.map((str) => {
    const [locationId, roleId, date] = str.split('|');
    return { locationId, roleId, date };
  }),
);

export const selectAvailability = createSelector(selectStaffingSlice, (s) => s.availability);

export const selectAvailabilityDateData = createSelector(selectAvailability, (availability) => availability.dateAvailability);

export const selectWorkerAvailabilitySummary = createSelector([selectAvailabilityDateData, selectInRangeDates], (dateAvailability, dates) => {
  const summary: Record<string, { available: number; total: number }> = {};
  for (const dateData of dateAvailability) {
    if (!dates.includes(dateData.date)) continue;
    for (const worker of dateData.workers) {
      if (!summary[worker.evUserId]) {
        summary[worker.evUserId] = { available: 0, total: 0 };
      }
      summary[worker.evUserId].total++;
      if (worker.status === 'available') {
        summary[worker.evUserId].available++;
      }
    }
  }
  return summary;
});

export const selectSurveyDraggedWorkers = createSelector(selectStaffingSlice, (s) => s.surveyDraggedWorkers);

export const selectIsSurveyDragOver = createSelector(selectStaffingSlice, (s) => s.isSurveyDragOver);

export const selectIsSurveyDragging = createSelector(selectStaffingSlice, (s) => s.surveyDraggedWorkers.length > 0);

export const selectSurveySelectedWorkerIds = createSelector(selectStaffingSlice, (s) => s.surveySelectedWorkerIds);

export const selectWorkerPickerDocked = createSelector(selectStaffingSlice, (s) => s.workerPickerDocked);

export const selectActivePickerCell = createSelector(selectStaffingSlice, (s) => s.activePickerCell);

export const selectSidebarActiveTab = createSelector(selectStaffingSlice, (s) => s.sidebarActiveTab);

export const selectConfirmationSidebarFilter = createSelector(selectStaffingSlice, (s) => s.confirmationSidebarFilter);

export const selectHighlightedCell = createSelector(selectStaffingSlice, (s) => s.highlightedCell);

export const selectConfirmationGroupBy = createSelector(selectStaffingSlice, (s) => s.confirmationGroupBy);

export const selectSelectedScheduleIds = createSelector(selectStaffingSlice, (s) => s.selectedScheduleIds);

export const selectConfirmationSaving = createSelector(selectStaffingSlice, (s) => s.confirmationSaving);

export const selectConfirmationSaveError = createSelector(selectStaffingSlice, (s) => s.confirmationSaveError);

export const selectUpdatingScheduleIds = createSelector(selectStaffingSlice, (s) => s.updatingScheduleIds);

export const selectSurveyWorkerFilters = createSelector(selectStaffingSlice, (s) => s.surveyWorkerFilters);

export const selectSurveyDayFilters = createSelector(selectStaffingSlice, (s) => s.surveyDayFilters);

export const selectSurveySortBy = createSelector(selectStaffingSlice, (s) => s.surveySortBy);

export const selectSurveySortDir = createSelector(selectStaffingSlice, (s) => s.surveySortDir);

export const selectSurveyFiltersActive = createSelector(
  [selectSurveyWorkerFilters, selectSurveyDayFilters],
  (workerFilters, dayFilters) => workerFilters.length > 0 || dayFilters.length > 0,
);

export const selectSurveyFilterCount = createSelector([selectSurveyWorkerFilters, selectSurveyDayFilters], (workerFilters, dayFilters) => workerFilters.length + dayFilters.length);

export const selectRogueConversionDialog = createSelector(selectStaffingSlice, (s) => s.rogueConversionDialog);

export const selectMimicMode = createSelector(selectStaffingSlice, (s) => s.mimicMode);

export const selectMimicPhase = createSelector(selectMimicMode, (mimic) => mimic.phase);

export const selectMimicSourceType = createSelector(selectMimicMode, (mimic) => mimic.sourceType);

export const selectMimicSource = createSelector(selectMimicMode, (mimic) => mimic.source);

export const selectMimicSelectedTargets = createSelector(selectMimicMode, (mimic) => mimic.selectedTargets);

export const selectIsMimicModeActive = createSelector(selectMimicPhase, (phase) => phase !== MimicPhase.IDLE);

export const selectMimicTargetCount = createSelector(selectMimicSelectedTargets, (targets) => targets.length);

export const selectIsMimicTargetSelected = createSelector([selectMimicMode], (mimic) => (target: { locationId?: string; roleId?: string; date?: string }): boolean => {
  if (mimic.phase !== MimicPhase.SELECTING_TARGETS) return false;

  return mimic.selectedTargets.some((t) => {
    if (mimic.sourceType === 'location') {
      return t.locationId === target.locationId;
    }
    if (mimic.sourceType === 'role') {
      return t.locationId === target.locationId && t.roleId === target.roleId;
    }
    if (mimic.sourceType === 'date') {
      return t.date === target.date;
    }
    return false;
  });
});

export const selectMimicReplaceMode = createSelector(selectMimicMode, (mimic) => mimic.replaceMode);

export const selectLastSaveResult = createSelector(selectStaffingSlice, (staffing) => staffing.lastSaveResult);

export const selectIsSourceDateEarlyVoting = createSelector([selectMimicMode, selectElectionDate], (mimic, electionDate) => {
  if (mimic.sourceType !== 'date' || !mimic.source?.date) return false;
  return mimic.source.date < electionDate;
});

export const selectAllEligibleLocationTargets = createSelector([selectStaffingChartData, selectMimicMode], (locations, mimic) => {
  if (mimic.sourceType !== 'location' || !mimic.source?.locationId) return [];
  return locations.filter((l) => l.locationId !== mimic.source?.locationId).map((l) => ({ locationId: l.locationId }));
});

export const selectAllEligibleDateTargets = createSelector([selectInRangeDates, selectMimicMode], (dates, mimic) => {
  if (mimic.sourceType !== 'date' || !mimic.source?.date) return [];
  return dates.filter((d) => d !== mimic.source?.date).map((d) => ({ date: d }));
});

export const selectAllEarlyVotingDateTargets = createSelector([selectInRangeDates, selectMimicMode, selectElectionDate], (dates, mimic, electionDate) => {
  if (mimic.sourceType !== 'date' || !mimic.source?.date) return [];
  return dates.filter((d) => d !== mimic.source?.date && d < electionDate).map((d) => ({ date: d }));
});

export const selectAllMatchingRoleTargets = createSelector([selectStaffingChartData, selectMimicMode], (locations, mimic) => {
  if (mimic.sourceType !== 'role' || !mimic.source?.roleId) return [];
  const sourceRoleId = mimic.source.roleId;
  const sourceLocationId = mimic.source.locationId;
  const targets: { locationId: string; roleId: string }[] = [];
  for (const location of locations) {
    for (const role of location.roles) {
      if (role.isRogue || role.isAdded) continue;
      if (role.roleId === sourceRoleId) {
        if (location.locationId !== sourceLocationId) {
          targets.push({ locationId: location.locationId, roleId: role.roleId });
        }
      }
    }
  }
  return targets;
});

export const selectMultiSelectScheduleImpact = createSelector([selectMultiSelectSelectedCells, selectSchedules], (selectedCells, schedules) => {
  if (selectedCells.length === 0) return 0;
  let count = 0;
  for (const cellKey of selectedCells) {
    const [locationId, roleId, date] = cellKey.split('|');
    const normalizedDate = normalizeWorkDate(date);
    for (const schedule of schedules) {
      if (schedule.locationId === locationId && schedule.roleId === roleId && normalizeWorkDate(schedule.workDate) === normalizedDate) {
        count++;
      }
    }
  }
  return count;
});

export const selectMimicScheduleImpact = createSelector([selectMimicMode, selectSchedules, selectInRangeDates], (mimic, schedules, dates) => {
  const defaultResult = { affectedWorkerCount: 0, affectedCellCount: 0, affectedWorkers: [] as { name: string; partyCode?: string; cellKey: string }[] };
  if (!mimic?.selectedTargets?.length) {
    return defaultResult;
  }

  const affectedWorkers: { name: string; partyCode?: string; cellKey: string }[] = [];
  const affectedCellKeys = new Set<string>();

  if (mimic.sourceType === 'location') {
    for (const target of mimic.selectedTargets) {
      if (!target.locationId) continue;
      for (const schedule of schedules) {
        if (schedule.locationId === target.locationId && dates.includes(schedule.workDate)) {
          const cellKey = `${schedule.locationId}|${schedule.roleId}|${schedule.workDate}`;
          affectedCellKeys.add(cellKey);
          affectedWorkers.push({
            name: `${schedule.evUser.firstName} ${schedule.evUser.lastName}`,
            partyCode: schedule.party?.partyCode,
            cellKey,
          });
        }
      }
    }
  }

  if (mimic.sourceType === 'role') {
    for (const target of mimic.selectedTargets) {
      if (!target.locationId || !target.roleId) continue;
      for (const schedule of schedules) {
        if (schedule.locationId === target.locationId && schedule.roleId === target.roleId && dates.includes(schedule.workDate)) {
          const cellKey = `${schedule.locationId}|${schedule.roleId}|${schedule.workDate}`;
          affectedCellKeys.add(cellKey);
          affectedWorkers.push({
            name: `${schedule.evUser.firstName} ${schedule.evUser.lastName}`,
            partyCode: schedule.party?.partyCode,
            cellKey,
          });
        }
      }
    }
  }

  if (mimic.sourceType === 'date') {
    for (const target of mimic.selectedTargets) {
      if (!target.date) continue;
      const normalizedTargetDate = normalizeWorkDate(target.date);
      for (const schedule of schedules) {
        if (normalizeWorkDate(schedule.workDate) === normalizedTargetDate) {
          const cellKey = `${schedule.locationId}|${schedule.roleId}|${normalizeWorkDate(schedule.workDate)}`;
          affectedCellKeys.add(cellKey);
          affectedWorkers.push({
            name: `${schedule.evUser.firstName} ${schedule.evUser.lastName}`,
            partyCode: schedule.party?.partyCode,
            cellKey,
          });
        }
      }
    }
  }

  return {
    affectedWorkerCount: affectedWorkers.length,
    affectedCellCount: affectedCellKeys.size,
    affectedWorkers,
  };
});

export const makeSelectWorkerScheduledDates = () =>
  createSelector([selectSchedules, (_: AppState, evUserId: string) => evUserId], (schedules, evUserId) => {
    return new Set(schedules.filter((s) => s.evUser.id === evUserId).map((s) => s.workDate));
  });

export const selectSameDaySchedulesByDate = createSelector([selectSchedules], (schedules) => {
  const byDate = new Map<string, Map<string, SameDayScheduleRef[]>>();

  for (const schedule of schedules) {
    const ref = toSameDayScheduleRef(schedule);
    let byWorker = byDate.get(ref.date);
    if (!byWorker) {
      byWorker = new Map();
      byDate.set(ref.date, byWorker);
    }

    const existing = byWorker.get(ref.evUserId);
    if (existing) {
      existing.push(ref);
    } else {
      byWorker.set(ref.evUserId, [ref]);
    }
  }

  return byDate;
});

export const makeSelectSameDaySchedulesForDate = () =>
  createSelector([selectSameDaySchedulesByDate, (_: AppState, date: string) => normalizeWorkDate(date)], (byDate, date) => byDate.get(date) ?? EMPTY_SAME_DAY_SCHEDULES);
